import { BaseProps } from '../props/base/base-props';
import { cloneDeep } from '../utils/clone-deep';
import { MutationSource } from '../constants/mutation-constants';
import { PresentationEventTypes } from '../manager/event-manager/event-types';
import { ChangeType } from './observable-data-model';
import type { IEventManager } from '../manager/event-manager';
import type { DeepPartial, DeepReadonly, DeepWritable } from 'ts-essentials';
import type { MutationAPI } from '../api/mutation-api';
import type { DataType, IData } from '../data';
import type { DeltaOf } from '../utils/delta';
import type { IBaseContext } from '../context/base-context';
import type {
    IObservableDataModel,
    DataModelChangeSubscriber,
    DataModelChange,
    SubscribeOptions,
} from './observable-data-model';
import type { InheritPropsTypes } from '../utils/on-props-change';

export interface IDataModel {
    id: string;
    type: DataType;
    parent: IDataModel;

    attach(): void;
    detach(shouldDispose?: boolean): void;
    dispose(): void;
}

export type IStore<Data extends IData = IData> = {
    data: Data;

    patchData: (diff: DeepReadonly<DeltaOf<Data>>) => void;
    onPropsChange: (changedPaths?: InheritPropsTypes[], needEnqueueChange?: boolean) => void;

    /**
     * 获取继承链路上的所有子页面
     */
    getInheritChildPages: () => unknown[];
    /**
     * 获取继承链路上的所有子形状
     */
    getInheritChildShapes: () => unknown[];

    dispose(): void;
};

export abstract class DataModel<
    Data extends IData = IData,
    Props extends DeepPartial<DeepReadonly<Data>> = DeepPartial<DeepReadonly<Data>>,
> implements IDataModel, IObservableDataModel<Data, Props>
{
    private subscribers = new Map<DataModelChangeSubscriber<Data, Props>, { priority: number }>();

    public abstract parent: IDataModel;
    public abstract store: IStore<any>;
    public abstract props: Props;
    public abstract api: MutationAPI<any>;
    public abstract context: IBaseContext;

    constructor(public readonly events: IEventManager) {}

    public get id(): string {
        return this.store.data.id;
    }

    public get type(): Data['type'] {
        return this.store.data.type;
    }

    public getOwnedData(): DeepReadonly<Data> {
        return this.store.data as DeepReadonly<Data>;
    }

    public getWritableOwnedData() {
        return this.store.data as DeepWritable<Data>;
    }

    public getInheritedData(): Props {
        return BaseProps.getInheritedData(this.getBaseProps());
    }

    public getCloneData(): Data {
        return cloneDeep(this.store.data);
    }

    public subscribe(subscriber: DataModelChangeSubscriber<Data, Props>, options: SubscribeOptions = {}): () => void {
        this.subscribers.set(subscriber, { priority: options.priority ?? 0 });

        if (options.initialChange) {
            this.notify({
                type: ChangeType.Init,
                source: MutationSource.None,
                diffs: {
                    data: this.store.data,
                    props: this.props as DeltaOf<Props>,
                },
            });
        }

        return () => {
            this.unsubscribe(subscriber);
        };
    }

    public unsubscribe(subscriber: DataModelChangeSubscriber<Data, Props>): void {
        this.subscribers.delete(subscriber);
    }

    public notify(...changes: DataModelChange<Data, Props>[]) {
        Array.from(this.subscribers.entries())
            .sort(([, { priority: priorityA }], [, { priority: priorityB }]) => priorityB - priorityA)
            .forEach(([subscriber]) => {
                changes.forEach((change) => {
                    subscriber(change);
                });
            });
    }

    public attach(): this {
        this.events.emit(PresentationEventTypes.AttachDataModel, this);
        return this;
    }

    public detach(): this {
        this.events.emit(PresentationEventTypes.DetachDataModel, this);
        return this;
    }

    public dispose(): void {
        this.subscribers.clear();
        this.getBaseProps().dispose();
        this.store.dispose();
    }

    public resourceSubscribe(id: string) {
        this.events.emit(PresentationEventTypes.ModelObserveResourceMap, this, id);
    }

    public forEachDescendant(callback: (child: RestrictedDataModel) => void = () => void 0) {
        void callback;
    }

    public getDescendants(): RestrictedDataModel[] {
        const descendants: RestrictedDataModel[] = [];
        this.forEachDescendant((descendant) => descendants.push(descendant));
        return descendants;
    }

    protected getBaseProps(): BaseProps<Props> {
        return this.props as BaseProps<Props>;
    }
}

export type RestrictedDataModel<Model extends IDataModel = DataModel> = Omit<
    Model,
    'store' | 'parent' | 'attach' | 'detach' | 'dispose' | 'notify' | 'context' | 'events'
> & {
    parent: RestrictedDataModel<Model['parent']>;
};
